import { useMemo } from 'react';
import { css } from '@emotion/react';
import { WritebackSettingMode } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import {
  useGetWritebackCapabilitiesQuery,
  useUpdateWritebackSettingsMutation,
} from '@frontend/api-call-intelligence-writeback';
import { checkForPMSWritebackACL, useIsCallIntelEligibleAccount } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Accordion, Button, ContentLoader, Heading, useAlert, useModalControl } from '@frontend/design-system';
import { CALL_SUMMARY_SYNC_SETTING_NAME } from '../../constants';
import { CallIntelSettingsTrackingIds } from '../../tracking-ids';
import { LocationWiseSyncSettingsMap, SyncSettingsMap } from '../../types';
import { UnauthorizedAccess } from '../unauthorized-access';
import { CallSummaryPmsSyncSettingsForm } from './call-summary-pms-sync-settings-form';
import { MultiPmsSyncSettingsModal } from './multi-pms-sync-settings-modal';

export const CallIntelPmsSyncSettings = () => {
  const { t } = useTranslation('settings', { keyPrefix: 'call-intel-settings' });

  const { getLocationName, isSingleTypeScope } = useAppScopeStore();
  const { activeLocationIds, isLoading: isLoadingActiveLocationIds } = useIsCallIntelEligibleAccount();
  const { modalProps, openModal } = useModalControl();
  const alerts = useAlert();

  const allowedLocationIds = useMemo(() => {
    return activeLocationIds.filter((locationId) => checkForPMSWritebackACL({ locationId }));
  }, [activeLocationIds]);

  const canManageAllLocations = allowedLocationIds.length > 0 && allowedLocationIds.length === activeLocationIds.length;

  const { mutate: updateWritebackSettings, isLoading: isUpdatingSettings } = useUpdateWritebackSettingsMutation({
    options: {
      onSuccess: () => {
        alerts.success(t('Settings updated successfully.'));
      },
      onError: (error) => {
        alerts.error(t('Failed to update settings.'));
        sentry.warn({
          error: `Error updating Call Intelligence PMS sync settings: ${error}`,
          topic: 'analytics',
          team: 'analytics',
          addContext: {
            name: 'call-intel-pms-sync-settings',
            context: { error },
          },
        });
      },
    },
  });

  const {
    data: writebackCapabilitiesData,
    isLoading: isLoadingWritebackCapabilities,
    isFetching: isFetchingWritebackCapabilities,
  } = useGetWritebackCapabilitiesQuery({
    request: {
      locationIds: allowedLocationIds,
    },
    options: {
      enabled: allowedLocationIds.length > 0,
    },
  });

  const { syncEligibleLocationIds, locationWiseSettingsMap } = useMemo(() => {
    const syncEligibleLocations =
      writebackCapabilitiesData?.capabilities?.filter(
        ({ capability, locationId }) => capability?.isCapable && locationId
      ) || [];

    const _syncEligibleLocationIds = syncEligibleLocations
      .map(({ locationId }) => locationId ?? '')
      .sort((a, b) => getLocationName(a).localeCompare(getLocationName(b)));

    // Create location wise - setting name wise - settings map
    const _locationWiseSettingsMap = syncEligibleLocations.reduce<LocationWiseSyncSettingsMap>((acc, current) => {
      if (current.locationId && current.settings) {
        acc[current.locationId] = current.settings.reduce<SyncSettingsMap>((settingsMap, currentSettings) => {
          if (currentSettings.settingName) {
            settingsMap[currentSettings.settingName] = currentSettings;
          }
          return settingsMap;
        }, {});
      }
      return acc;
    }, {});

    return {
      syncEligibleLocationIds: _syncEligibleLocationIds,
      locationWiseSettingsMap: _locationWiseSettingsMap,
    };
  }, [getLocationName, writebackCapabilitiesData?.capabilities]);

  const handleSettingsUpdate = ({ mode, locationId }: { mode: WritebackSettingMode; locationId?: string }) => {
    const locationsToUpdate = locationId ? [locationId] : syncEligibleLocationIds;

    updateWritebackSettings({
      data: locationsToUpdate.map((locId) => ({
        locationId: locId,
        settings: [{ ...locationWiseSettingsMap[locId][CALL_SUMMARY_SYNC_SETTING_NAME], mode }],
      })),
    });
  };

  if (isLoadingActiveLocationIds || (allowedLocationIds.length > 0 && isLoadingWritebackCapabilities)) {
    return (
      <div css={[styles.loadingCard, styles.container]}>
        <ContentLoader show />
      </div>
    );
  }

  if (!isLoadingActiveLocationIds && allowedLocationIds.length === 0) {
    return (
      <div css={styles.container}>
        <UnauthorizedAccess containerStyles={styles.singleLocationCard} />
      </div>
    );
  }

  if (!syncEligibleLocationIds.length) {
    return (
      <div css={styles.container}>
        <UnauthorizedAccess
          containerStyles={styles.singleLocationCard}
          title={t('No sync eligible locations found.')}
        />
      </div>
    );
  }

  if (isSingleTypeScope && syncEligibleLocationIds.length === 1) {
    const singleLocationId = syncEligibleLocationIds[0];
    return (
      <div css={styles.container}>
        <CallSummaryPmsSyncSettingsForm
          locationId={singleLocationId}
          containerStyles={styles.singleLocationCard}
          syncSettingCardStyles={styles.singleLocationSyncCard}
          onSave={handleSettingsUpdate}
          isLoading={isUpdatingSettings || isFetchingWritebackCapabilities}
          value={locationWiseSettingsMap[singleLocationId][CALL_SUMMARY_SYNC_SETTING_NAME]?.mode}
        />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Heading level={3}>{t('Sync Settings')}</Heading>
        <Button
          variant='tertiary'
          trackingId={CallIntelSettingsTrackingIds.pmsSync.manageAllButton}
          onClick={openModal}
          disabled={!canManageAllLocations}
        >
          {t('Manage All')}
        </Button>
      </div>

      <Accordion
        variant='location'
        showBoxShadow
        chevronSize={16}
        size='large'
        startingValue={syncEligibleLocationIds[0]}
      >
        {syncEligibleLocationIds.map((locationId) => (
          <Accordion.Item value={locationId} key={locationId} data-testid={`accordion-item-${locationId}`}>
            <Accordion.Header>
              <Accordion.Header.Location title={getLocationName(locationId)} />
            </Accordion.Header>
            <Accordion.Body css={{ padding: 0 }}>
              <CallSummaryPmsSyncSettingsForm
                locationId={locationId}
                onSave={handleSettingsUpdate}
                isLoading={isUpdatingSettings || isFetchingWritebackCapabilities}
                value={locationWiseSettingsMap[locationId][CALL_SUMMARY_SYNC_SETTING_NAME]?.mode}
              />
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
      <MultiPmsSyncSettingsModal
        {...modalProps}
        locationIds={syncEligibleLocationIds}
        locationWiseSyncSettingsMap={locationWiseSettingsMap}
        onSave={handleSettingsUpdate}
      />
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    max-width: 700px;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  singleLocationCard: css`
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    border-radius: ${theme.borderRadius.medium};
  `,
  loadingCard: css`
    position: relative;
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    border-radius: ${theme.borderRadius.medium};
    height: 150px;
    width: 100%;
    overflow: hidden;
  `,
  singleLocationSyncCard: css`
    padding: ${theme.spacing(4)};
    border: none;
    margin: 0;
  `,
};
